/* ============================================================================
 * nydo · Stores page map (store locator v2, built on the 29 Sept 2026 release)
 * File: assets/stores-page-map.js
 * Pairs with: sections/stores-page-map.liquid
 *             assets/section-stores-page-map.css
 *
 * Responsibilities
 *  1. Open / closed state and details (the buttons are rendered by the
 *     Liquid; this file only builds them when a container arrives empty)
 *  2. Search, type and country filters, result count, "near me" sorting
 *  3. Leaflet map: loaded when it scrolls into view, type-coloured pins,
 *     fly-to on selection, refit when the filters change
 *  4. Self-guided visit drawer (Wunschtag + Zeitfenster, then the Shopify
 *     Forms app block), unchanged from the 29 Sept release
 *  5. Push every interaction to window.dataLayer with a store_id
 *
 * Tracking contract (unchanged event names and parameters):
 *   store_locator_view, store_filter_use, store_map_pin_click,
 *   store_card_expand, store_directions_click, store_phone_click,
 *   store_model_click, booking_open, booking_slot_selected, booking_abandon,
 *   store_visit_form_submit, store_visit_click, online_consultation_click
 * Additive only: storeParams() also sends location_type.
 * ==========================================================================*/
(function () {
  'use strict';

  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
  var LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
  var WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  var DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mo first
  var PHONE_VISIBLE = 5;

  window.dataLayer = window.dataLayer || [];

  /* ---------------------------------------------------------------- helpers */

  function qs(el, sel) { return el.querySelector(sel); }
  function qsa(el, sel) { return Array.prototype.slice.call(el.querySelectorAll(sel)); }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  /* lowercase + strip accents, so "zurich" finds "Zürich" */
  function norm(s) {
    s = String(s || '').toLowerCase();
    try { s = s.normalize('NFD').replace(/[̀-ͯ]/g, ''); } catch (e) { /* old browser */ }
    return s.replace(/ß/g, 'ss');
  }

  /* "+49 (0) 89 379 155 99" -> "tel:+498937915599" */
  function telHref(phone) {
    if (!phone) return '';
    var s = String(phone).trim();
    var intl = s.charAt(0) === '+';
    if (intl) s = s.replace(/\(\s*0\s*\)/g, '');
    return 'tel:' + (intl ? '+' : '') + s.replace(/[^\d]/g, '');
  }

  function parseHM(s) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
  }

  function parseRange(s) {
    var parts = String(s || '').trim().split('-');
    if (parts.length !== 2) return null;
    var a = parseHM(parts[0]), b = parseHM(parts[1]);
    return a == null || b == null ? null : { from: a, to: b, label: parts[0].trim() + ' – ' + parts[1].trim() };
  }

  function parseLines(raw) {
    return String(raw || '')
      .split('\n')
      .map(function (l) { return l.trim(); })
      .filter(Boolean)
      .map(function (l) {
        var bits = l.split('|');
        return { label: bits[0].trim(), value: (bits[1] || '').trim() };
      });
  }

  function parseClosures(raw) {
    var out = {};
    parseLines(raw).forEach(function (row) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(row.label)) return;
      out[row.label] = parseRange(row.value) ? row.value : null;
    });
    return out;
  }

  function nowIn(tz) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz || 'Europe/Berlin', weekday: 'short', hour: '2-digit', minute: '2-digit',
        year: 'numeric', month: '2-digit', day: '2-digit', hour12: false
      }).formatToParts(new Date());
    } catch (e) {
      var d = new Date();
      return {
        day: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes(),
        iso: d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
      };
    }
    var g = {};
    parts.forEach(function (p) { g[p.type] = p.value; });
    var hour = g.hour === '24' ? 0 : parseInt(g.hour, 10);
    return { day: WD[g.weekday], minutes: hour * 60 + parseInt(g.minute, 10), iso: g.year + '-' + g.month + '-' + g.day };
  }

  function hoursFor(store, dayIndex, isoDate) {
    if (isoDate && Object.prototype.hasOwnProperty.call(store._closures, isoDate)) {
      var override = store._closures[isoDate];
      return override ? parseRange(override) : null;
    }
    return parseRange(store.hours[String(dayIndex)]);
  }

  function fmtMinutes(min) { return pad(Math.floor(min / 60)) + ':' + pad(min % 60); }

  function hasHours(store) {
    for (var d = 0; d < 7; d++) if (parseRange(store.hours[String(d)])) return true;
    return false;
  }

  function statusFor(store, L) {
    /* No opening hours entered: say nothing rather than "closed every day" */
    if (store.coming_soon) return { open: false, text: '' };
    if (!hasHours(store)) return { open: false, text: store.hours_text || '', info: true };
    var now = nowIn(store.timezone);
    var today = hoursFor(store, now.day, now.iso);
    if (today && now.minutes >= today.from && now.minutes < today.to) {
      return { open: true, text: L.open_until + ' ' + fmtMinutes(today.to) };
    }
    if (today && now.minutes < today.from) {
      return { open: false, text: L.opens + ' heute ' + fmtMinutes(today.from) };
    }
    var days = L.days.split(',');
    for (var i = 1; i <= 7; i++) {
      var d = (now.day + i) % 7;
      var h = hoursFor(store, d, null);
      if (h) return { open: false, text: L.closed_today + ', ' + L.opens.toLowerCase() + ' ' + days[d] + ' ' + fmtMinutes(h.from) };
    }
    return { open: false, text: L.closed_today };
  }

  function openOnDate(store, isoDate) {
    var d = new Date(isoDate + 'T12:00:00');
    if (isNaN(d.getTime())) return null;
    return hoursFor(store, d.getDay(), isoDate);
  }

  function groupHours(store, L, keepClosed) {
    var groups = [];
    DISPLAY_ORDER.forEach(function (d) {
      var val = parseRange(store.hours[String(d)]);
      if (!val && !keepClosed) return;
      var text = val ? val.label : L.closed;
      var last = groups[groups.length - 1];
      if (last && last.text === text) last.days.push(d);
      else groups.push({ text: text, days: [d] });
    });
    return groups;
  }

  function dayName(g, L) {
    var DAYS = L.days.split(',');
    return g.days.length === 1
      ? DAYS[g.days[0]]
      : DAYS[g.days[0]] + ' ' + L.day_join + ' ' + DAYS[g.days[g.days.length - 1]];
  }

  function hoursSummaryLine(store, L) {
    var groups = groupHours(store, L, false);
    if (!groups.length) return '';
    return L.hours + ': ' + groups.map(function (g) { return dayName(g, L) + ' ' + g.text; }).join(', ');
  }

  function routeUrl(store) {
    if (store.maps_url) return store.maps_url;
    var dest = store.address || (store.lat && store.lng ? store.lat + ',' + store.lng : '');
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
  }

  function haversine(a, b, c, d) {
    var R = 6371, p = Math.PI / 180;
    var dLat = (c - a) * p, dLng = (d - b) * p;
    var x = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a * p) * Math.cos(c * p) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }

  function consentState() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) { /* consent API not ready */ }
    return null;
  }

  function marketCountry() {
    try { if (window.Shopify && window.Shopify.country) return window.Shopify.country; } catch (e) { /* not Shopify */ }
    return null;
  }

  /* Leaflet is loaded once per page and shared by every section that needs it */
  function loadLeaflet(done) {
    if (window.L && window.L.map) { done(); return; }
    var q = window.__pelLeafletQueue = window.__pelLeafletQueue || [];
    q.push(done);
    if (window.__pelLeafletLoading) return;
    window.__pelLeafletLoading = true;
    var pending = 2, failed = false;
    function flush(err) {
      if (err) window.__pelLeafletLoading = false;
      q.splice(0).forEach(function (fn) { fn(err); });
    }
    function ok() { if (!failed && --pending === 0) flush(); }
    function bad() { if (!failed) { failed = true; flush(new Error('leaflet')); } }
    var css = document.createElement('link');
    css.rel = 'stylesheet'; css.href = LEAFLET_CSS; css.integrity = LEAFLET_CSS_SRI; css.crossOrigin = '';
    css.onload = ok; css.onerror = bad;
    document.head.appendChild(css);
    var js = document.createElement('script');
    js.src = LEAFLET_JS; js.integrity = LEAFLET_JS_SRI; js.crossOrigin = '';
    js.onload = ok; js.onerror = bad;
    document.head.appendChild(js);
  }

  /* ------------------------------------------------------------ the locator */

  function Locator(root) {
    var cfgNode = qs(root, '[data-nydo-config]');
    if (!cfgNode) return;
    var cfg;
    try { cfg = JSON.parse(cfgNode.textContent); } catch (e) { return; }

    var L = cfg.labels;
    var DAYS = L.days.split(',');
    var opts = cfg.options || {};
    var stores = {};
    var order = [];
    var geoSorted = false;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var wide = window.matchMedia('(min-width: 990px)');
    var phone = window.matchMedia('(max-width: 749px)');
    var focusZoom = opts.focus_zoom || 14;

    var list = qs(root, '[data-nydo-list]');
    var mapCard = qs(root, '[data-nydo-mapcard]');
    var mapEl = qs(root, '[data-nydo-map]');
    var mapErr = qs(root, '[data-nydo-map-error]');
    var hint = qs(root, '[data-nydo-touch-hint]');
    var searchEl = qs(root, '[data-nydo-search]');
    var countryEl = qs(root, '[data-nydo-country]');
    var chipsWrap = qs(root, '[data-nydo-chips]');
    var countEl = qs(root, '[data-nydo-count]');
    var emptyEl = qs(root, '[data-nydo-empty]');

    var map = null, mapStarted = false, activeId = null;
    var markersById = {};
    var filter = { type: 'all', country: 'all', q: '' };

    cfg.stores.forEach(function (s) {
      s.type = s.type || 'showroom';
      s._closures = parseClosures(s.closures_raw);
      s._models = parseLines(s.models_raw);
      s._lat = parseFloat(s.lat);
      s._lng = parseFloat(s.lng);
      stores[s.id] = s;
    });

    function cardOf(id) { return qs(root, '[data-nydo-store="' + id + '"]'); }
    function cards() { return qsa(root, '[data-nydo-store]'); }
    cards().forEach(function (c) { c._hay = norm(c.getAttribute('data-search')); });

    /* Filters: the markup lists every type and country; keep the ones in use,
       and drop a filter that would only have one choice. */
    (function () {
      var types = {}, countries = {};
      cards().forEach(function (c) {
        types[c.getAttribute('data-type')] = 1;
        countries[c.getAttribute('data-country')] = 1;
      });
      if (chipsWrap) {
        qsa(chipsWrap, '[data-nydo-type]').forEach(function (b) {
          var t = b.getAttribute('data-nydo-type');
          if (t !== 'all' && !types[t]) b.parentNode.removeChild(b);
        });
        if (Object.keys(types).length < 2) { chipsWrap.parentNode.removeChild(chipsWrap); chipsWrap = null; }
        else chipsWrap.setAttribute('data-ready', '');
      }
      if (countryEl) {
        qsa(countryEl, 'option').forEach(function (o) {
          if (o.value !== 'all' && !countries[o.value]) o.parentNode.removeChild(o);
        });
        var cw = countryEl.closest('[data-nydo-country-wrap]') || countryEl;
        if (Object.keys(countries).length < 2) {
          var bar = cw.parentNode;
          bar.removeChild(cw);
          countryEl = null;
          if (bar && !bar.children.length) bar.parentNode.removeChild(bar);
        } else cw.setAttribute('data-ready', '');
      }
    })();

    /* No location with coordinates (e.g. all "coming soon"): no empty map, list only */
    var anyPin = cfg.stores.some(function (s) { return !s.coming_soon && !isNaN(s._lat) && !isNaN(s._lng); });
    if (!anyPin && mapCard) {
      mapCard.hidden = true;
      var lm = mapCard.closest('.pel-locmap');
      if (lm) { lm.classList.remove('pel-map--true'); lm.classList.add('pel-map--false'); }
    }
    /* No locations at all: the empty text without the reset link */
    if (!cards().length) {
      var rs = qs(root, '[data-nydo-reset]');
      if (rs) rs.hidden = true;
    }

    /* Theme editor only: list locations that miss information */
    var banner = qs(root, '[data-nydo-editor-banner]');
    if (banner && window.Shopify && window.Shopify.designMode) {
      var rows = [];
      cfg.stores.forEach(function (s, i) {
        var miss = [];
        var hrs = s.hours_text || Object.keys(s.hours || {}).some(function (k) { return s.hours[k]; });
        if (!s.name) miss.push('name');
        if (!s.coming_soon) {
          if (!s.address) miss.push('address');
          if (!s.lat || !s.lng) miss.push('latitude/longitude (no pin on the map)');
          if (!hrs) miss.push('opening hours (no status, no self-guided booking)');
          if (s.cta && s.cta.visit_guided && !s.visit_guided_url) miss.push('guided-visit link (button hidden)');
        }
        if (s.id === s.block_id) miss.push('store_id (tracking key)');
        if (miss.length) rows.push((s.name || 'Location ' + (i + 1)) + ': ' + miss.join(', '));
      });
      if (rows.length) {
        var bp = el('p');
        bp.appendChild(el('strong', null, 'Only visible in the theme editor.'));
        bp.appendChild(document.createTextNode(' Some locations are missing information:'));
        banner.appendChild(bp);
        var ul = el('ul');
        rows.forEach(function (r) { ul.appendChild(el('li', null, r)); });
        banner.appendChild(ul);
        banner.appendChild(el('p', null, 'Click a location block on the left to fill it in. Default booking links live in the "Booking and buttons" block.'));
        banner.hidden = false;
      }
    }

    /* ---------------------------------------------------- type order */
    /* List order: locations with a "Show at the top" position first (1, 2, 3 …),
       then the rest by type order, then by editor order. */
    var typeOrder = String(opts.type_order || '').split(',').filter(Boolean);
    if (list) {
      var blockOrder = cards();
      var topOf = function (c) { var v = parseInt(c.getAttribute('data-top'), 10); return isNaN(v) ? Infinity : v; };
      var rank = function (c) { var r = typeOrder.indexOf(c.getAttribute('data-type')); return r < 0 ? typeOrder.length : r; };
      blockOrder.slice().sort(function (a, b) {
        var ta = topOf(a), tb = topOf(b);
        if (ta !== tb) return ta < tb ? -1 : 1;
        return (rank(a) - rank(b)) || (blockOrder.indexOf(a) - blockOrder.indexOf(b));
      }).forEach(function (c) { list.appendChild(c); });
      if (chipsWrap) {
        typeOrder.forEach(function (t) {
          var chip = qs(chipsWrap, '[data-nydo-type="' + t + '"]');
          if (chip) chipsWrap.appendChild(chip);
        });
      }
    }
    order = cards().map(function (c) { return c.getAttribute('data-nydo-store'); });

    /* --------------------------------------------------------------- events */

    function push(event, params) {
      var payload = { event: event };
      Object.keys(params || {}).forEach(function (k) { payload[k] = params[k]; });
      window.dataLayer.push(payload);
      if (opts.debug && window.console) console.log('[nydo] ' + event, payload);
    }

    function storeParams(s, extra) {
      var p = {
        store_id: s.id,
        store_name: s.name,
        store_country: s.country,
        location_type: s.type,
        position: order.indexOf(s.id) + 1
      };
      Object.keys(extra || {}).forEach(function (k) { p[k] = extra[k]; });
      return p;
    }

    /* ---------------------------------------------------------- card render */

    function renderStatus(s) {
      var card = cardOf(s.id);
      if (!card) return;
      var node = qs(card, '[data-nydo-status]');
      if (!node) return; /* coming-soon cards have no status */
      var st = statusFor(s, L);
      node.textContent = st.text;
      node.classList.toggle('pel-loc__status--shut', !st.open && !st.info);
      node.classList.toggle('pel-loc__status--info', !!st.info);
      s._isOpenNow = st.open;
    }

    function ctaBtn(tag, primary, label, main) {
      var n = el(tag, 'btn ' + (primary ? 'btn--primary' : 'btn--secondary') + ' pel-btn-layout' + (main ? ' nydo-cta--main' : ''), label);
      if (tag === 'button') n.type = 'button';
      return n;
    }

    function renderCtas(s) {
      var card = cardOf(s.id);
      if (!card) return;
      /* On the card, or inside the details when the section shows the short note */
      var wrap = qs(card, '[data-nydo-ctas]');
      if (!wrap || wrap.children.length) return; /* already rendered by the Liquid */
      var primaryUsed = false;

      if (s.cta.visit_self) {
        var vs = ctaBtn('button', true, L.visit_self, true);
        vs.setAttribute('data-nydo-book', s.id);
        wrap.appendChild(vs);
        primaryUsed = true;
      }
      if (s.cta.visit_guided && s.visit_guided_url) {
        var vg = ctaBtn('a', !primaryUsed, L.visit_guided, true);
        vg.href = s.visit_guided_url;
        vg.rel = 'noopener';
        vg.setAttribute('data-nydo-visit', s.id);
        vg.setAttribute('data-visit-type', 'guided');
        wrap.appendChild(vg);
        primaryUsed = true;
      }
      if (s.cta.consultation && s.consultation_url) {
        var c = ctaBtn('a', false, L.consultation, false);
        c.href = s.consultation_url;
        c.rel = 'noopener';
        c.setAttribute('data-nydo-consult', s.id);
        wrap.appendChild(c);
      }
      if (s.cta.route) {
        var r = ctaBtn('a', !primaryUsed, L.route, !primaryUsed);
        r.href = routeUrl(s);
        r.target = '_blank';
        r.rel = 'noopener';
        r.setAttribute('data-nydo-route', s.id);
        wrap.appendChild(r);
      }
      if (s.cta.phone && s.phone) {
        var p = ctaBtn('a', false, L.call, false);
        p.href = telHref(s.phone);
        p.setAttribute('data-nydo-call', s.id);
        wrap.appendChild(p);
      }
    }

    function field(label, valueNode) {
      var f = el('div', 'pel-loc__field');
      f.appendChild(el('dt', 'pel-loc__label', label));
      valueNode.classList.add('pel-loc__value');
      f.appendChild(valueNode);
      return f;
    }

    function modelNode(m) {
      var node;
      if (m.value) {
        node = el('a', 'pel-loc__model', m.label);
        node.href = m.value;
        node.setAttribute('data-nydo-model', m.label);
      } else {
        node = el('span', 'pel-loc__model', m.label);
      }
      return node;
    }

    function renderDetails(s) {
      var card = cardOf(s.id);
      if (!card) return;
      var box = qs(card, '[data-nydo-details]');
      if (box.getAttribute('data-built')) return;
      box.setAttribute('data-built', '1');

      var now = nowIn(s.timezone);
      var dl = el('dl', 'pel-loc__fields');

      var rows = hasHours(s) ? el('dd', 'pel-loc__rows') : null;
      if (rows) groupHours(s, L, true).forEach(function (g) {
        var r = el('span', 'pel-loc__row' + (g.days.indexOf(now.day) > -1 ? ' is-today' : ''));
        r.appendChild(el('span', null, dayName(g, L)));
        r.appendChild(el('span', null, g.text));
        rows.appendChild(r);
      });
      if (rows) dl.appendChild(field(L.hours, rows));
      else if (s.hours_text) dl.appendChild(field(L.hours, el('dd', null, s.hours_text)));

      var closureKeys = Object.keys(s._closures).sort();
      if (closureKeys.length) {
        var crow = el('dd', 'pel-loc__rows');
        closureKeys.forEach(function (k) {
          var d = k.split('-');
          var r = el('span', 'pel-loc__row');
          r.appendChild(el('span', null, d[2] + '.' + d[1] + '.' + d[0]));
          r.appendChild(el('span', null, s._closures[k] ? parseRange(s._closures[k]).label : L.closed));
          crow.appendChild(r);
        });
        dl.appendChild(field(L.closures, crow));
      }

      var strip = qs(box, '[data-nydo-products]');
      if (strip) {
        /* picked products: photo + name cards instead of the text list */
        var sd = el('dd', 'pel-loc__value--products');
        sd.appendChild(strip);
        dl.appendChild(field(L.models, sd));
      } else if (s._models.length) {
        var mw = el('dd', 'pel-loc__models');
        s._models.forEach(function (m) { mw.appendChild(modelNode(m)); });
        dl.appendChild(field(L.models, mw));
      }

      if (s.phone) {
        var pd = el('dd');
        var pa = el('a', 'pel-loc__tel', s.phone);
        pa.href = telHref(s.phone);
        pa.setAttribute('data-nydo-call', s.id);
        pd.appendChild(pa);
        dl.appendChild(field(L.phone, pd));
      }

      if (s.notes) {
        var nd = el('dd', 'pel-loc__notes');
        nd.appendChild(el('p', null, s.notes));
        dl.appendChild(field(L.notes, nd));
      }

      /* photos stay on top, the buttons (note mode) stay below the details */
      var gal = qs(box, '[data-nydo-gallery]');
      box.insertBefore(dl, gal ? gal.nextSibling : box.firstChild);
    }

    function setExpanded(card, on) {
      cards().forEach(function (c) {
        var t = qs(c, '[data-nydo-toggle]');
        var d = qs(c, '[data-nydo-details]');
        if (!t || !d) return; /* coming-soon cards have no details */
        var open = c === card ? on : (on ? false : t.getAttribute('aria-expanded') === 'true');
        if (open) renderDetails(stores[c.getAttribute('data-nydo-store')]);
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
        qs(t, '[data-nydo-toggle-label]').textContent = open ? L.details_close : L.details_open;
        d.hidden = !open;
      });
    }

    /* ------------------------------------------------------- list helpers */

    function fitPhoneList() {
      if (!list) return;
      var vis = cards().filter(function (c) { return !c.hidden; });
      if (true || !phone.matches || vis.length <= PHONE_VISIBLE) { list.style.maxHeight = ''; return; }
      var last = vis[PHONE_VISIBLE - 1];
      list.style.maxHeight = (last.offsetTop + last.offsetHeight + 3) + 'px';
    }

    function scrollListTo(card) {
      if (!list || list.scrollHeight <= list.clientHeight + 1) return;
      var lr = list.getBoundingClientRect(), cr = card.getBoundingClientRect();
      if (cr.top < lr.top || cr.height > lr.height) list.scrollTop += cr.top - lr.top - 4;
      else if (cr.bottom > lr.bottom) list.scrollTop += cr.bottom - lr.bottom + 4;
    }

    /* ----------------------------------------------------------- selection */

    function syncActive() {
      Object.keys(markersById).forEach(function (id) {
        var mk = markersById[id], node = mk.getElement && mk.getElement();
        var on = id === activeId;
        if (node) node.classList.toggle('is-active', on);
        mk.setZIndexOffset(on ? 1000 : 0);
      });
    }

    function select(id, o) {
      o = o || {};
      var card = cardOf(id);
      if (!card) return;
      activeId = id;
      cards().forEach(function (c) { c.classList.toggle('is-active', c === card); });
      if (o.expand) setExpanded(card, true);
      if (o.scrollList) scrollListTo(card);
      var mapSeen = true;
      if (mapCard) {
        var mr = mapCard.getBoundingClientRect();
        mapSeen = mr.bottom > 0 && mr.top < window.innerHeight;
      }
      if (map) {
        syncActive();
        var mk = markersById[id];
        if (mk && map.hasLayer(mk)) {
          var z = Math.max(map.getZoom(), focusZoom);
          if (reduce || !mapSeen) map.setView(mk.getLatLng(), z, { animate: false });
          else map.flyTo(mk.getLatLng(), z, { duration: 0.8 });
          mk.openPopup();
        }
      }
      if (!o.fromMap && !o.stay && !wide.matches && mapCard && map) {
        var r = mapCard.getBoundingClientRect();
        if (r.top < 0 || r.bottom > window.innerHeight) {
          mapCard.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        }
      }
    }

    /* ------------------------------------------------------------- filters */

    function fitMap(instant) {
      if (!map) return;
      var pts = [];
      Object.keys(markersById).forEach(function (id) {
        if (map.hasLayer(markersById[id])) pts.push(markersById[id].getLatLng());
      });
      if (!pts.length) return;
      var animate = !reduce && !instant;
      if (pts.length === 1) map.setView(pts[0], Math.max(focusZoom - 2, 10), { animate: animate });
      else map.fitBounds(pts, { padding: [48, 48], maxZoom: 12, animate: animate });
    }

    function applyFilters(refit, instant) {
      var terms = norm(filter.q).trim().split(/\s+/).filter(Boolean);
      var visible = 0;
      cards().forEach(function (c) {
        var ok = (filter.type === 'all' || c.getAttribute('data-type') === filter.type) &&
                 (filter.country === 'all' || c.getAttribute('data-country') === filter.country);
        if (ok && terms.length) ok = terms.every(function (t) { return c._hay.indexOf(t) > -1; });
        c.hidden = !ok;
        if (ok) visible++;
        var id = c.getAttribute('data-nydo-store');
        var mk = markersById[id];
        if (map && mk) {
          if (ok && !map.hasLayer(mk)) mk.addTo(map);
          if (!ok && map.hasLayer(mk)) map.removeLayer(mk);
        }
        if (!ok && id === activeId) {
          activeId = null;
          c.classList.remove('is-active');
          if (map) map.closePopup();
        }
      });
      if (map) syncActive();
      if (countEl) {
        var tpl = visible === 1 ? countEl.getAttribute('data-one') : countEl.getAttribute('data-many');
        countEl.textContent = visible ? String(tpl || '{n}').replace('{n}', visible) : '';
      }
      if (emptyEl) emptyEl.hidden = visible > 0;
      if (refit) fitMap(instant);
      fitPhoneList();
    }

    function setTypeChip(value) {
      if (!chipsWrap) return;
      qsa(chipsWrap, '[data-nydo-type]').forEach(function (o) {
        var on = o.getAttribute('data-nydo-type') === value;
        o.setAttribute('aria-pressed', on ? 'true' : 'false');
        o.classList.toggle('btn--primary', on);
        o.classList.toggle('btn--secondary', !on);
      });
    }

    function sortByDistance(lat, lng) {
      var scored = cfg.stores.map(function (s) {
        var km = (!isNaN(s._lat) && !isNaN(s._lng)) ? haversine(lat, lng, s._lat, s._lng) : null;
        return { s: s, km: km };
      }).sort(function (a, b) {
        if (a.km == null) return 1;
        if (b.km == null) return -1;
        return a.km - b.km;
      });
      order = scored.map(function (x) { return x.s.id; });
      geoSorted = true;
      scored.forEach(function (x) {
        var card = cardOf(x.s.id);
        if (!card) return;
        list.appendChild(card);
        var d = qs(card, '[data-nydo-dist]');
        if (x.km != null) {
          d.textContent = (x.km < 10 ? x.km.toFixed(1).replace('.', ',') : Math.round(x.km)) + ' ' + L.km;
          d.hidden = false;
        }
      });
      if (list) list.scrollTop = 0;
    }

    if (opts.geolocation && navigator.geolocation) {
      var geoWrap = chipsWrap;
      if (geoWrap) {
        var g = el('button', 'btn btn--secondary pel-btn-layout pel-locmap__filter pel-locmap__geo', L.geo);
        g.type = 'button';
        g.setAttribute('aria-pressed', 'false');
        g.setAttribute('data-nydo-geo', '1');
        geoWrap.appendChild(g);
      }
    }

    /* ---------------------------------------------------------------- map */

    function makeIcon(type, isNew, soon) {
      return window.L.divIcon({
        className: 'pel-locmap__pin pel-locmap__pin--' + type + (isNew ? ' pel-locmap__pin--new' : '') + (soon ? ' pel-locmap__pin--soon' : ''),
        html: (isNew || soon ? '<span class="pel-locmap__pin-glow" aria-hidden="true"></span>' : '') + '<svg viewBox="0 0 34 44" width="34" height="44" aria-hidden="true"><path d="M17 43C17 43 3 28 3 16.5C3 8.5 9.3 2 17 2S31 8.5 31 16.5C31 28 17 43 17 43Z"/><circle cx="17" cy="16.5" r="5.5"/></svg>',
        iconSize: [34, 44],
        iconAnchor: [17, 42],
        popupAnchor: [0, -38]
      });
    }

    function buildPopup(s) {
      var box = el('div', 'pel-locmap__popup');
      box.appendChild(el('strong', null, s.name));
      box.appendChild(el('span', null, s.address));
      if (s.cta.route) {
        var a = el('a', null, L.route);
        a.href = routeUrl(s);
        a.target = '_blank';
        a.rel = 'noopener';
        a.setAttribute('data-nydo-route', s.id);
        a.addEventListener('click', function () {
          push('store_directions_click', storeParams(s, { provider: 'google', is_open_now: !!s._isOpenNow }));
        });
        box.appendChild(a);
      }
      return box;
    }

    function initMap() {
      if (map || !window.L || !mapEl) return;
      var Lf = window.L;
      map = Lf.map(mapEl, {
        scrollWheelZoom: !!opts.wheel,
        dragging: !Lf.Browser.mobile,
        tap: !Lf.Browser.mobile,
        minZoom: 3,
        zoomSnap: 0.5
      });
      var tiles = mapEl.getAttribute('data-tiles');
      if (tiles) {
        Lf.tileLayer(tiles, {
          attribution: mapEl.getAttribute('data-attribution'),
          subdomains: mapEl.getAttribute('data-subdomains') || 'abc',
          maxZoom: 19
        }).addTo(map);
      }
      map.setView([49.5, 10.3], 6);

      if (Lf.Browser.mobile && hint) {
        var hintAt = 0, hintTimer = 0;
        mapEl.addEventListener('touchmove', function (ev) {
          var now = Date.now();
          if (ev.touches.length !== 1 || now - hintAt < 4000) return;
          hintAt = now;
          hint.classList.add('is-visible');
          clearTimeout(hintTimer);
          hintTimer = setTimeout(function () { hint.classList.remove('is-visible'); }, 1600);
        }, { passive: true });
      }

      cfg.stores.forEach(function (s) {
        if (isNaN(s._lat) || isNaN(s._lng) || Math.abs(s._lat) > 90 || Math.abs(s._lng) > 180) return;
        var mk = Lf.marker([s._lat, s._lng], { icon: makeIcon(s.type, s.is_new, s.coming_soon), title: s.name, alt: s.name, keyboard: false, riseOnHover: true });
        mk.bindPopup(function () { return buildPopup(s); }, { autoPan: false, maxWidth: 260 });
        mk.on('click', function () {
          push('store_map_pin_click', storeParams(s));
          select(s.id, { fromMap: true, scrollList: true });
          var card = cardOf(s.id);
          if (card && !wide.matches) card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
        });
        markersById[s.id] = mk;
      });

      map.invalidateSize();
      applyFilters(true, true);
      if (window.ResizeObserver) new ResizeObserver(function () { map.invalidateSize(); }).observe(mapEl);

      /* Lets the page (or a preview) react once the map exists */
      try { root.dispatchEvent(new CustomEvent('nydo:map-ready', { detail: { map: map } })); } catch (e) { /* old browser */ }
    }

    function startMap() {
      if (mapStarted || !mapEl) return;
      mapStarted = true;
      loadLeaflet(function (err) {
        if (err) {
          mapStarted = false;
          if (mapErr) mapErr.hidden = false;
          return;
        }
        if (mapErr) mapErr.hidden = true;
        initMap();
      });
    }

    function lazyStartMap() {
      if (!mapCard) return;
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (items) {
          if (items.some(function (i) { return i.isIntersecting; })) { io.disconnect(); startMap(); }
        }, { rootMargin: '300px 0px' });
        io.observe(mapCard);
      } else {
        startMap();
      }
    }

    /* ------------------------------------------------------- self-visit drawer */

    var drawer = qs(root, '[data-nydo-drawer]');
    var form = qs(root, '[data-nydo-form]');
    var appBlockWrap = qs(root, '[data-nydo-appblock]');
    var noAppBlockMsg = qs(root, '[data-nydo-no-appblock]');
    var state = { store: null, window: null, models: [], step: 1, submitted: false, calMonth: null, calBounds: null };
    var lastFocus = null;

    if (appBlockWrap && noAppBlockMsg && !appBlockWrap.textContent.trim() && !appBlockWrap.children.length) {
      noAppBlockMsg.hidden = false;
    }

    function setStep(n) {
      state.step = n;
      qsa(form, '[data-nydo-step]').forEach(function (s) {
        s.hidden = parseInt(s.getAttribute('data-nydo-step'), 10) !== n;
      });
      qsa(drawer, '[data-nydo-bar]').forEach(function (b) {
        b.classList.toggle('is-on', parseInt(b.getAttribute('data-nydo-bar'), 10) <= n);
      });
      form.scrollTop = 0;
    }

    function buildWindows() {
      var wrap = qs(form, '[data-nydo-windows]');
      wrap.textContent = '';
      parseLines(cfg.booking.windows).forEach(function (w) {
        var b = el('button', 'nydo-window');
        b.type = 'button';
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('data-nydo-window', w.label);
        b.setAttribute('data-nydo-window-time', w.value);
        b.appendChild(el('span', null, w.label));
        if (w.value) b.appendChild(el('small', null, w.value.replace('-', ' – ')));
        wrap.appendChild(b);
      });
    }

    function buildModelPicker(s) {
      var wrap = qs(form, '[data-nydo-modelpick]');
      wrap.textContent = '';
      state.models = [];
      var fieldWrap = qs(form, '[data-nydo-models-field]');
      if (!s._models.length) { fieldWrap.hidden = true; return; }
      fieldWrap.hidden = false;
      s._models.forEach(function (m) {
        var b = el('button', null, m.label);
        b.type = 'button';
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('data-nydo-pickmodel', m.label);
        wrap.appendChild(b);
      });
    }

    function isoOf(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }

    function dateBounds() {
      var input = qs(form, '[data-nydo-date]');
      var t = new Date();
      t.setHours(0, 0, 0, 0);
      var min = new Date(t.getTime() + (cfg.booking.lead_days || 0) * 864e5);
      var max = new Date(t.getTime() + (cfg.booking.max_days || 60) * 864e5);
      input.min = isoOf(min);
      input.max = isoOf(max);
      return { min: min, max: max };
    }

    function monthStart(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
    function sameMonth(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth(); }

    function renderCalendar() {
      var s = state.store;
      if (!s) return;
      var bounds = state.calBounds;
      var grid = qs(form, '[data-nydo-cal-grid]');
      var wk = qs(form, '[data-nydo-cal-weekdays]');
      var months = L.months ? L.months.split(',') : [];
      var todayIso = isoOf(new Date());
      var selectedIso = qs(form, '[data-nydo-date]').value;

      if (!wk.childNodes.length) DAYS.slice(1).concat(DAYS.slice(0, 1)).forEach(function (d) { wk.appendChild(el('span', null, d)); });
      qs(form, '[data-nydo-cal-month]').textContent = (months[state.calMonth.getMonth()] || '') + ' ' + state.calMonth.getFullYear();
      qs(form, '[data-nydo-cal-prev]').disabled = sameMonth(state.calMonth, monthStart(bounds.min));
      qs(form, '[data-nydo-cal-next]').disabled = sameMonth(state.calMonth, monthStart(bounds.max));

      grid.textContent = '';
      var first = monthStart(state.calMonth);
      for (var i = 0; i < (first.getDay() + 6) % 7; i++) grid.appendChild(el('span', 'nydo-cal__day nydo-cal__day--blank'));
      var daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
      for (var day = 1; day <= daysInMonth; day++) {
        var d = new Date(first.getFullYear(), first.getMonth(), day);
        var iso = isoOf(d);
        var b = el('button', 'nydo-cal__day', String(day));
        b.type = 'button';
        b.setAttribute('data-nydo-day', iso);
        var inRange = d >= bounds.min && d <= bounds.max;
        if (!(inRange && openOnDate(s, iso))) b.disabled = true;
        if (iso === todayIso) b.classList.add('nydo-cal__day--today');
        if (iso === selectedIso) b.classList.add('nydo-cal__day--selected');
        b.setAttribute('aria-pressed', iso === selectedIso ? 'true' : 'false');
        grid.appendChild(b);
      }
    }

    function openDrawer(id, entryPoint) {
      var s = stores[id];
      if (!s || !drawer) return;
      var bounds = dateBounds();
      state = { store: s, window: null, models: [], step: 1, submitted: false, calMonth: monthStart(bounds.min), calBounds: bounds };
      lastFocus = document.activeElement;

      qs(drawer, '[data-nydo-drawer-title]').textContent = s.name;
      qs(form, '[data-nydo-step1-lead]').textContent =
        (s.appointment_note ? s.appointment_note + '. ' : '') +
        'Wähle Deinen Wunschtag. Im nächsten Schritt wählst Du ein Zeitfenster.';
      qs(form, '[data-nydo-cal-hours]').textContent = hoursSummaryLine(s, L);
      qs(form, '[data-nydo-date]').value = '';
      buildWindows();
      buildModelPicker(s);
      renderCalendar();
      clearErrors();
      setStep(1);

      drawer.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var firstDay = qs(form, '[data-nydo-day]:not(:disabled)');
      if (firstDay) firstDay.focus();

      push('booking_open', storeParams(s, { entry_point: entryOverride || entryPoint || 'card' }));
    }

    function closeDrawer() {
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      document.documentElement.style.overflow = '';
      if (state.store && (state.window || state.step > 1) && !state.submitted) {
        push('booking_abandon', storeParams(state.store, { last_step: state.step }));
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function clearErrors() {
      qsa(form, '[data-nydo-err]').forEach(function (n) { n.hidden = true; n.textContent = ''; });
      qsa(form, '.nydo-field--invalid').forEach(function (n) { n.classList.remove('nydo-field--invalid'); });
    }

    function showError(key, msg) {
      var node = qs(form, '[data-nydo-err="' + key + '"]');
      if (!node) return;
      node.textContent = msg;
      node.hidden = false;
      var f = node.closest('.nydo-field');
      if (f) f.classList.add('nydo-field--invalid');
    }

    function formatDate(iso) {
      if (!iso) return '';
      var p = iso.split('-');
      return p[2] + '.' + p[1] + '.' + p[0];
    }

    function daysAhead(iso) {
      if (!iso) return null;
      var t = new Date();
      t.setHours(0, 0, 0, 0);
      return Math.round((new Date(iso + 'T00:00:00') - t) / 864e5);
    }

    /* -------------------------------------------------------------- wiring */

    /* ------------------------------------------------------- photo viewer */

    /* ------------------------------------------------ appointment chooser
       The "Termine" note books directly when a location has one booking option,
       and opens this popup to pick one when it has several. Each choice clicks the
       location's own button, so the tracking is the same as on the card
       (plus entry_point: appointments_note). */
    var entryOverride = null;
    var ch = qs(root, '[data-nydo-chooser]');
    var chList = ch && qs(ch, '[data-nydo-chooser-list]');
    var chReturn = null;

    function bookingCtas(card) {
      var box = qs(card, '[data-nydo-ctas]');
      return box ? qsa(box, '[data-nydo-book], [data-nydo-visit], [data-nydo-consult]') : [];
    }
    function runCta(btn) {
      entryOverride = 'appointments_note';
      try { btn.click(); } finally { entryOverride = null; }
    }
    function chClose() {
      if (ch && ch.open) ch.close();
    }
    function chOpen(card, ctas, from) {
      var s = stores[card.getAttribute('data-nydo-store')];
      qs(ch, '[data-nydo-chooser-store]').textContent = s ? s.name : '';
      chList.innerHTML = '';
      var ICONS = {
        self: '<circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.6"/><path d="M15.2 8.8l-1.9 4.5-4.5 1.9 1.9-4.5z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
        guided: '<circle cx="8.5" cy="8" r="2.6" stroke="currentColor" stroke-width="1.6"/><circle cx="16" cy="9" r="2.1" stroke="currentColor" stroke-width="1.6"/><path d="M3.8 19c.6-3 2.4-4.6 4.7-4.6s4.1 1.6 4.7 4.6M13.6 14.3c.7-.4 1.5-.6 2.4-.6 1.9 0 3.4 1.4 3.9 3.9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
        consult: '<rect x="3" y="6.5" width="12.5" height="11" rx="2.2" stroke="currentColor" stroke-width="1.6"/><path d="M15.5 10.5l5-3v9l-5-3" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>'
      };
      ctas.forEach(function (btn) {
        var kind = btn.hasAttribute('data-nydo-book') ? 'self' : btn.hasAttribute('data-nydo-consult') ? 'consult' : 'guided';
        var opt = el('button', 'nydo-chooser__opt nydo-chooser__opt--' + kind);
        opt.type = 'button';
        opt.insertAdjacentHTML('beforeend', '<span class="nydo-chooser__opt-icon" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none">' + ICONS[kind] + '</svg></span>');
        var txt = el('span', 'nydo-chooser__opt-text');
        txt.appendChild(el('span', 'nydo-chooser__opt-title', ch.getAttribute('data-t-' + kind) || btn.textContent.trim()));
        var desc = kind === 'self' && s && s.appointment_note ? s.appointment_note : ch.getAttribute('data-d-' + kind);
        if (desc) txt.appendChild(el('span', 'nydo-chooser__opt-desc', desc));
        opt.appendChild(txt);
        opt.insertAdjacentHTML('beforeend', '<span class="nydo-chooser__opt-go" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.5 8H12.5M12.5 8L8.5 4M12.5 8L8.5 12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>');
        opt.addEventListener('click', function () {
          chReturn = null;
          chClose();
          runCta(btn);
        });
        chList.appendChild(opt);
      });
      var foot = qs(ch, '[data-nydo-chooser-foot]');
      if (foot) {
        foot.innerHTML = '';
        if (s && s.address) {
          foot.insertAdjacentHTML('beforeend', '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" stroke="currentColor" stroke-width="1.6"/><circle cx="12" cy="10" r="2.3" stroke="currentColor" stroke-width="1.6"/></svg>');
          foot.appendChild(el('span', null, s.address));
        }
        foot.hidden = !(s && s.address);
      }
      chReturn = from;
      if (typeof ch.showModal === 'function') ch.showModal(); else ch.setAttribute('open', '');
      var first = qs(chList, 'button');
      if (first) first.focus();
    }
    if (ch) {
      qs(ch, '[data-nydo-chooser-close]').addEventListener('click', chClose);
      ch.addEventListener('click', function (ev) { if (ev.target === ch) chClose(); });
      ch.addEventListener('close', function () {
        if (chReturn && chReturn.focus) chReturn.focus();
        chReturn = null;
      });
    }

    var lb = qs(root, '[data-nydo-lightbox]');
    var lbImg = lb && qs(lb, '[data-nydo-lb-img]');
    var lbCap = lb && qs(lb, '[data-nydo-lb-cap]');
    var lbPhotos = [], lbIndex = 0, lbReturn = null;

    function lbShow(i) {
      if (!lbPhotos.length) return;
      lbIndex = (i + lbPhotos.length) % lbPhotos.length;
      var ph = lbPhotos[lbIndex];
      if (ph.getAttribute('data-w')) {
        lbImg.width = parseInt(ph.getAttribute('data-w'), 10) || 1800;
        lbImg.height = parseInt(ph.getAttribute('data-h'), 10) || 1350;
      }
      lbImg.src = ph.getAttribute('data-full');
      lbImg.alt = ph.getAttribute('data-alt') || '';
      lbCap.textContent = (ph.getAttribute('data-alt') || '') + (lbPhotos.length > 1 ? '  ·  ' + (lbIndex + 1) + ' / ' + lbPhotos.length : '');
      lb.classList.toggle('is-single', lbPhotos.length < 2);
    }

    function lbOpen(photo) {
      if (!lb) return;
      var gallery = photo.closest('[data-nydo-gallery]');
      lbPhotos = gallery ? qsa(gallery, '[data-nydo-photo]') : [photo];
      lbReturn = photo;
      lbShow(lbPhotos.indexOf(photo));
      if (typeof lb.showModal === 'function') lb.showModal(); else lb.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
    }

    function lbClose() {
      if (!lb) return;
      if (typeof lb.close === 'function' && lb.open) lb.close(); else lb.removeAttribute('open');
    }

    if (lb) {
      lb.addEventListener('close', function () {
        document.documentElement.style.overflow = '';
        lbImg.removeAttribute('src');
        if (lbReturn && lbReturn.focus) lbReturn.focus();
      });
      lb.addEventListener('click', function (ev) {
        if (ev.target === lb || ev.target.closest('[data-nydo-lb-close]')) { lbClose(); return; }
        if (ev.target.closest('[data-nydo-lb-prev]')) { lbShow(lbIndex - 1); return; }
        if (ev.target.closest('[data-nydo-lb-next]')) { lbShow(lbIndex + 1); }
      });
      lb.addEventListener('keydown', function (ev) {
        if (ev.key === 'ArrowLeft') lbShow(lbIndex - 1);
        if (ev.key === 'ArrowRight') lbShow(lbIndex + 1);
      });
      /* swipe on phones */
      var touchX = null;
      lb.addEventListener('touchstart', function (ev) { touchX = ev.touches[0].clientX; }, { passive: true });
      lb.addEventListener('touchend', function (ev) {
        if (touchX == null) return;
        var dx = ev.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) > 40) lbShow(lbIndex + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    root.addEventListener('click', function (ev) {
      if (drawer && drawer.contains(ev.target)) return;
      if (lb && lb.contains(ev.target)) return;
      if (ch && ch.contains(ev.target)) return;
      var photo = ev.target.closest('[data-nydo-photo]');
      if (photo && root.contains(photo)) { lbOpen(photo); return; }
      var t = ev.target.closest('[data-nydo-book], [data-nydo-visit], [data-nydo-route], [data-nydo-call],' +
        '[data-nydo-consult], [data-nydo-model], [data-nydo-toggle], [data-nydo-note], [data-nydo-geo], [data-nydo-type],' +
        '[data-nydo-consultation-global], [data-nydo-reset], [data-nydo-store]');
      if (!t || !root.contains(t)) return;
      if (t.closest('.leaflet-container')) return; /* popups track their own clicks */
      var id, s;

      if ((id = t.getAttribute('data-nydo-book'))) { openDrawer(id, 'card'); return; }
      if ((id = t.getAttribute('data-nydo-visit'))) {
        var vp = { visit_type: t.getAttribute('data-visit-type'), destination: t.getAttribute('href') };
        if (entryOverride) vp.entry_point = entryOverride;
        push('store_visit_click', storeParams(stores[id], vp));
        return;
      }
      if ((id = t.getAttribute('data-nydo-route'))) {
        s = stores[id];
        push('store_directions_click', storeParams(s, { provider: 'google', is_open_now: !!s._isOpenNow }));
        return;
      }
      if ((id = t.getAttribute('data-nydo-call'))) {
        s = stores[id];
        push('store_phone_click', storeParams(s, { is_open_now: !!s._isOpenNow }));
        return;
      }
      if ((id = t.getAttribute('data-nydo-consult'))) {
        push('online_consultation_click', storeParams(stores[id], {
          entry_point: entryOverride || 'store_card', destination: t.getAttribute('href')
        }));
        return;
      }
      if (t.hasAttribute('data-nydo-consultation-global')) {
        push('online_consultation_click', { store_id: null, entry_point: 'no_store_nearby', destination: t.getAttribute('href') });
        return;
      }
      if (t.hasAttribute('data-nydo-reset')) {
        clearTimeout(searchTimer); clearTimeout(trackTimer);
        filter.type = 'all'; filter.country = 'all'; filter.q = '';
        if (searchEl) searchEl.value = '';
        if (countryEl) countryEl.value = 'all';
        setTypeChip('all');
        applyFilters(true);
        if (searchEl) searchEl.focus();
        return;
      }
      if (t.hasAttribute('data-nydo-type')) {
        filter.type = t.getAttribute('data-nydo-type');
        setTypeChip(filter.type);
        applyFilters(true);
        push('store_filter_use', { filter_value: filter.type, method: 'type' });
        return;
      }
      if (t.hasAttribute('data-nydo-model')) {
        var mc = t.closest('[data-nydo-store]');
        s = stores[mc && mc.getAttribute('data-nydo-store')];
        if (s) push('store_model_click', storeParams(s, { model: t.getAttribute('data-nydo-model') }));
        return;
      }
      if (t.hasAttribute('data-nydo-note')) {
        var nc = t.closest('[data-nydo-store]');
        var bctas = bookingCtas(nc);
        /* one way to book: go straight there; several: let the visitor choose */
        if (bctas.length === 1) { runCta(bctas[0]); return; }
        if (bctas.length > 1 && ch) { chOpen(nc, bctas, t); return; }
        /* fallback: open the details, where the booking buttons are */
        var tg = qs(nc, '[data-nydo-toggle]');
        if (tg.getAttribute('aria-expanded') !== 'true') {
          setExpanded(nc, true);
          s = stores[nc.getAttribute('data-nydo-store')];
          push('store_card_expand', storeParams(s, { is_open_now: !!s._isOpenNow, entry_point: 'appointments_note' }));
          select(s.id, { scrollList: true, stay: true });
        }
        var firstCta = qs(nc, '[data-nydo-details] [data-nydo-ctas] .btn');
        if (firstCta) {
          firstCta.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
          firstCta.focus({ preventScroll: true });
        }
        return;
      }
      if (t.hasAttribute('data-nydo-toggle')) {
        var c = t.closest('[data-nydo-store]');
        var open = t.getAttribute('aria-expanded') !== 'true';
        setExpanded(c, open);
        if (open) {
          s = stores[c.getAttribute('data-nydo-store')];
          push('store_card_expand', storeParams(s, { is_open_now: !!s._isOpenNow }));
          select(s.id, { scrollList: true, stay: true });
        }
        return;
      }
      if (t.hasAttribute('data-nydo-geo')) {
        t.disabled = true;
        navigator.geolocation.getCurrentPosition(function (pos) {
          t.disabled = false;
          t.setAttribute('aria-pressed', 'true');
          t.classList.add('btn--primary');
          t.classList.remove('btn--secondary');
          sortByDistance(pos.coords.latitude, pos.coords.longitude);
          applyFilters(false);
          push('store_filter_use', { filter_value: 'nearest', method: 'geolocation' });
        }, function () {
          t.disabled = false;
          push('store_filter_use', { filter_value: 'nearest', method: 'geolocation_denied' });
        }, { timeout: 8000, maximumAge: 600000 });
        return;
      }
      if ((id = t.getAttribute('data-nydo-store'))) {
        /* the card itself (not one of its buttons, links or opened details): zoom the map to it */
        if (ev.target.closest('[data-nydo-details]')) return;
        select(id, {});
      }
    });

    if (countryEl) {
      countryEl.addEventListener('change', function () {
        filter.country = countryEl.value;
        applyFilters(true);
        push('store_filter_use', { filter_value: filter.country, method: 'country' });
      });
    }

    /* Search filters as you type; the event is sent once the visitor pauses,
       so one search is one event, not one per keystroke. */
    if (searchEl) {
      var searchTimer = 0, trackTimer = 0, lastTracked = '';
      searchEl.addEventListener('input', function () {
        clearTimeout(searchTimer);
        clearTimeout(trackTimer);
        searchTimer = setTimeout(function () {
          filter.q = searchEl.value;
          applyFilters(true);
        }, 180);
        trackTimer = setTimeout(function () {
          var v = norm(searchEl.value).trim().slice(0, 60);
          if (!v || v === lastTracked) return;
          lastTracked = v;
          push('store_filter_use', { filter_value: v, method: 'search' });
        }, 1200);
      });
    }

    if (drawer) {
      drawer.addEventListener('click', function (ev) {
        var t = ev.target.closest('[data-nydo-drawer-close], [data-nydo-next], [data-nydo-back], [data-nydo-window], [data-nydo-pickmodel],' +
          '[data-nydo-cal-prev], [data-nydo-cal-next], [data-nydo-day]');
        if (!t) return;

        if (t.hasAttribute('data-nydo-drawer-close')) { closeDrawer(); return; }

        if (t.hasAttribute('data-nydo-cal-prev') || t.hasAttribute('data-nydo-cal-next')) {
          if (t.disabled) return;
          var dir = t.hasAttribute('data-nydo-cal-next') ? 1 : -1;
          state.calMonth = new Date(state.calMonth.getFullYear(), state.calMonth.getMonth() + dir, 1);
          renderCalendar();
          return;
        }
        if (t.hasAttribute('data-nydo-day')) {
          if (t.disabled) return;
          qs(form, '[data-nydo-date]').value = t.getAttribute('data-nydo-day');
          renderCalendar();
          clearErrors();
          var sel = qs(form, '.nydo-cal__day--selected');
          if (sel) sel.focus();
          return;
        }
        if (t.hasAttribute('data-nydo-window')) {
          qsa(drawer, '[data-nydo-window]').forEach(function (b) { b.setAttribute('aria-pressed', b === t ? 'true' : 'false'); });
          state.window = t.getAttribute('data-nydo-window');
          var d = qs(form, '[data-nydo-date]').value;
          push('booking_slot_selected', storeParams(state.store, {
            preferred_date: d, preferred_window: state.window, days_ahead: daysAhead(d)
          }));
          return;
        }
        if (t.hasAttribute('data-nydo-pickmodel')) {
          var label = t.getAttribute('data-nydo-pickmodel');
          var on = t.getAttribute('aria-pressed') === 'true';
          t.setAttribute('aria-pressed', on ? 'false' : 'true');
          if (on) state.models = state.models.filter(function (m) { return m !== label; });
          else state.models.push(label);
          return;
        }
        if (t.hasAttribute('data-nydo-back')) {
          clearErrors();
          setStep(Math.max(1, state.step - 1));
          return;
        }
        if (t.hasAttribute('data-nydo-next')) {
          clearErrors();
          var dateVal = qs(form, '[data-nydo-date]').value;
          if (state.step === 1) {
            if (!dateVal) { showError('date', L.err_date || L.err_required); return; }
            if (!openOnDate(state.store, dateVal)) { showError('date', L.closed_that_day); return; }
            qs(form, '[data-nydo-step2-lead]').textContent = 'Wunschtag: ' + formatDate(dateVal) + '. Wähle jetzt ein Zeitfenster.';
            var dh = openOnDate(state.store, dateVal);
            qsa(form, '[data-nydo-window]').forEach(function (b) {
              var wr = parseRange(b.getAttribute('data-nydo-window-time'));
              var off = !!(dh && wr && (wr.from >= dh.to || wr.to <= dh.from));
              b.disabled = off;
              b.hidden = off;
              if (off && state.window === b.getAttribute('data-nydo-window')) {
                state.window = null;
                b.setAttribute('aria-pressed', 'false');
              }
            });
            setStep(2);
            var fw = qs(form, '[data-nydo-window]:not([disabled])');
            if (fw) fw.focus();
            return;
          }
          if (state.step === 2) {
            if (!state.window) { showError('window', L.err_required); return; }
            qs(form, '[data-nydo-recap]').textContent = state.store.name + ' · ' + formatDate(dateVal) + ' · ' + state.window +
              (state.models.length ? ' · ' + state.models.join(', ') : '');
            setStep(3);
            if (appBlockWrap) {
              var focusable = qs(appBlockWrap, 'input, textarea, select, button');
              if (focusable) focusable.focus();
            }
          }
        }
      });

      /* Best-effort submit tracking for the Forms app block (capture phase,
         before the app's own handler can stop the event). */
      drawer.addEventListener('submit', function (ev) {
        if (!appBlockWrap || !ev.target || !appBlockWrap.contains(ev.target)) return;
        if (state.submitted) return;
        state.submitted = true;
        var d = qs(form, '[data-nydo-date]').value;
        push('store_visit_form_submit', storeParams(state.store, {
          preferred_date: d, preferred_window: state.window, days_ahead: daysAhead(d),
          models_of_interest: state.models.join('|')
        }));
        setStep(4);
        var x = qs(drawer, '.nydo-drawer__x');
        if (x) x.focus();
      }, true);

      /* Listens on the document, not the drawer: after the Forms block is
         submitted its button disappears and focus falls back to <body>,
         where a drawer-level listener would no longer hear Escape. */
      document.addEventListener('keydown', function (ev) {
        if (drawer.hidden) return;
        if (ev.key === 'Escape' || ev.keyCode === 27) { closeDrawer(); return; }
        if (ev.key !== 'Tab' && ev.keyCode !== 9) return;
        var f = qsa(drawer, 'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]),' +
          ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])').filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (ev.shiftKey) {
          if (document.activeElement === first || !drawer.contains(document.activeElement)) { ev.preventDefault(); last.focus(); }
        } else if (document.activeElement === last || !drawer.contains(document.activeElement)) {
          ev.preventDefault(); first.focus();
        }
      }, true);
    }

    root.addEventListener('shopify:block:select', function (ev) {
      var card = ev.target && ev.target.closest ? ev.target.closest('[data-nydo-store]') : null;
      if (card) select(card.getAttribute('data-nydo-store'), { expand: true, scrollList: true });
    });

    var fitTimer = 0;
    window.addEventListener('resize', function () { clearTimeout(fitTimer); fitTimer = setTimeout(fitPhoneList, 150); });

    /* -------------------------------------------------------------- render */

    cfg.stores.forEach(function (s) {
      renderStatus(s);
      renderCtas(s);
    });
    applyFilters(false);

    if (opts.map && mapEl) lazyStartMap();

    setInterval(function () { cfg.stores.forEach(renderStatus); }, 300000);

    /* one locator view per page load, when the list is actually seen */
    var fired = false;
    function fireView() {
      if (fired) return;
      fired = true;
      push('store_locator_view', {
        stores_shown: cfg.stores.length,
        country: marketCountry(),
        is_geo_sorted: geoSorted,
        consent_analytics: consentState()
      });
    }
    if ('IntersectionObserver' in window && list) {
      var vio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { fireView(); vio.disconnect(); } });
      }, { threshold: 0.15 });
      vio.observe(list);
    } else {
      fireView();
    }
  }

  /* Scroll reveal, same as the Stores page map section */
  function reveal(root) {
    if (!root.classList.contains('pel-anim') || !window.innerHeight) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.remove('is-pending'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    qsa(root, '.pel-rv').forEach(function (n) {
      if (n.getBoundingClientRect().top > window.innerHeight * 0.92) { n.classList.add('is-pending'); io.observe(n); }
    });
  }

  function boot() {
    qsa(document, '[data-nydo-locator]').forEach(function (root) {
      if (root.getAttribute('data-nydo-ready')) return;
      root.setAttribute('data-nydo-ready', '1');
      Locator(root);
      reveal(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
